import AddressForm from "Components/Setting/AddressForm";
import GeneralForm from "Components/Setting/GeneralForm";
import useCheckRole from "Hooks/APICheckRole";
import { useAccount } from "Hooks/Endpoints";
import APISettingProvider from "Providers/APISettingProvider";
import React, { useState } from "react";
import { Container, Grid, Menu } from "semantic-ui-react";
import AccountChannels from "./AccountChannels";
import AccountDeleteForm from "./AccountDeleteForm";
import AccountLocale from "./AccountLocale";
import AccountRecording from "./AccountRecording";
import AccountTeams from "./AccountTeams";
import AccountVoiceCluster from "./AccountVoiceCluster";

function AccountSettings() {
  const { hasRole, roles } = useCheckRole();

  const {
    account,
    update,
    updateAddress,
    validateName,
    validateDomain,
    remove,
    select,
  } = useAccount();

  const getView = () => {
    const hasGeneral = hasRole(roles.account.settings.general.roles.edit);
    const hasAddress = hasRole(roles.account.settings.address.roles.edit);
    const hasLocale = hasRole(roles.account.settings.locale.roles.edit);
    const hasRecording = hasRole(roles.account.settings.recording.roles.edit);
    const hasConcurrency = hasRole(
      roles.account.settings.concurrency.roles.edit
    );
    const hasTeams = hasRole(roles.account.settings.teams.roles.edit);
    const hasDelete = hasRole(roles.account.roles.delete);
    if (hasGeneral) return "general";
    if (hasAddress) return "address";
    if (hasLocale) return "advanced";
    if (hasRecording) return "recording";
    if (hasConcurrency) return "concurrency";
    if (hasTeams) return "teams";
    if (hasDelete) return "delete";
    return "noRoles";
  };

  const [activeMenu, setActiveMenu] = useState<string>(getView());

  const selectMenu = (value: string) => {
    select(account.id);
    setActiveMenu(value);
  };

  return (
    <Container fluid>
      <Grid padded stackable>
        <Grid.Column width={4}>
          <Menu vertical fluid pointing>
            {hasRole(roles.account.settings.general.roles.edit) && (
              <Menu.Item
                name='general'
                onClick={() => selectMenu("general")}
                active={activeMenu === "general"}
              >
                General
              </Menu.Item>
            )}
            {hasRole(roles.account.settings.address.roles.edit) && (
              <Menu.Item
                name='address'
                onClick={() => selectMenu("address")}
                active={activeMenu === "address"}
              >
                Address
              </Menu.Item>
            )}
            {hasRole(roles.account.settings.voiceCluster.roles.edit) && (
              <Menu.Item
                name='voiceCluster'
                onClick={() => selectMenu("voiceCluster")}
                active={activeMenu === "voiceCluster"}
              >
                Voice Cluster
              </Menu.Item>
            )}
            {hasRole(roles.account.settings.recording.roles.edit) && (
              <Menu.Item
                name='recording'
                onClick={() => selectMenu("recording")}
                active={activeMenu === "recording"}
              >
                Recording
              </Menu.Item>
            )}

            {hasRole(roles.account.settings.concurrency.roles.edit) && (
              <Menu.Item
                name='concurrency'
                onClick={() => selectMenu("concurrency")}
                active={activeMenu === "concurrency"}
              >
                Concurrency
              </Menu.Item>
            )}

            {hasRole(roles.account.settings.teams.roles.edit) && (
              <Menu.Item
                name='teams'
                onClick={() => selectMenu("teams")}
                active={activeMenu === "teams"}
              >
                MS Teams
              </Menu.Item>
            )}
            {hasRole(roles.account.settings.locale.roles.edit) && (
              <Menu.Item
                name='advanced'
                onClick={() => selectMenu("advanced")}
                active={activeMenu === "advanced"}
              >
                Advanced
              </Menu.Item>
            )}
            {hasRole(roles.account.roles.delete) && (
              <Menu.Item
                name='delete'
                onClick={() => selectMenu("delete")}
                active={activeMenu === "delete"}
              >
                Delete
              </Menu.Item>
            )}
          </Menu>
        </Grid.Column>
        <Grid.Column width={12}>
          {activeMenu === "general" && (
            <GeneralForm
              account={account}
              update={update}
              validateDomain={validateDomain}
              validateName={validateName}
            />
          )}
          {activeMenu === "advanced" && (
            <APISettingProvider>
              <AccountLocale account={account} />
            </APISettingProvider>
          )}
          {activeMenu === "recording" && (
            <APISettingProvider>
              <AccountRecording account={account} />
            </APISettingProvider>
          )}
          {activeMenu === "concurrency" && (
            <APISettingProvider>
              <AccountChannels account={account} />
            </APISettingProvider>
          )}
          {activeMenu === "voiceCluster" && (
            <APISettingProvider>
              <AccountVoiceCluster account={account} />
            </APISettingProvider>
          )}
          {activeMenu === "teams" && (
            <APISettingProvider>
              <AccountTeams account={account} />
            </APISettingProvider>
          )}
          {activeMenu === "address" && (
            <AddressForm account={account} updateAddress={updateAddress} />
          )}
          {activeMenu === "delete" && (
            <AccountDeleteForm account={account} remove={remove} />
          )}
        </Grid.Column>
      </Grid>
    </Container>
  );
}

export default AccountSettings;
